import moment from "moment";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Form } from "react-bootstrap";
import { useSelector } from "react-redux";
import { get_cookie } from "utils";
import { getMinBookableHour, isTimeBookable } from "utils/reservation";


function TimePicker({
  defaultValue,
  parentTimeChangeHandler,
  currentObjName,
  selectedDate,
  setStartTimeDefaultValue,
  setEndTimeDefaultValue,
  vehicleSlug,
  selectedStartDate,
  selectedEndDate,
  startTimeDefaultValue,
  endTimeDefaultValue,
  setNoSlotError,
  setIsReserveEnable,
  isVehicleEdit,
  isShowCalendar
}) {
  // State management
  const [filteredTimes, setFilteredTimes] = useState([]);
  const [selectedValue, setSelectedValue] = useState(defaultValue || "");

  // Refs for tracking previous values to optimize re-renders
  const hasInitialized = useRef(false);
  const prevStartDate = useRef();
  const prevEndDate = useRef();
  const prevStartTime = useRef();

  // Redux state
  const { vehicleSlot } = useSelector(({ vehicle }) => vehicle);

  // Constants
  const PREFERRED_DEFAULT_TIMES = ["10:00", "9:00", "8:00"];

  // Memoized values
  const startTimeHour = useMemo(() => {
    if (!startTimeDefaultValue) return null;
    const hour = parseInt(startTimeDefaultValue.split(":")[0], 10);
    return isNaN(hour) ? null : hour;
  }, [startTimeDefaultValue]);

  /**
   * Generates filtered time options based on availability and constraints
   * @param {string} date - Selected date
   * @param {Object} availableDates - Available date slots
   * @returns {Array} Filtered time options
   */
  const getFilteredTimeOptions = useCallback((date, availableDates) => {
    if (!date || !availableDates) return [];

    const selectDate = moment(date, "MMM DD, YYYY").format("YYYY-MM-DD");
    const dateSlots = availableDates[selectDate];

    if (!dateSlots) return [];

    // Evaluated on every call (not memoized at mount) so a page left open
    // doesn't keep offering slots that have since moved into the past.
    const minBookableHour = getMinBookableHour(selectDate);
    const isSameStartEndDate = selectedStartDate === selectedEndDate;

    const availableTimes = Object.entries(dateSlots)
      .filter(([hour, slots]) => {
        const hourNum = Number(hour);
        const hasSlots = slots !== 0;
        const isAfterMinTime = hourNum > minBookableHour;
        const isAfterStartTime = !isSameStartEndDate || !startTimeHour || hourNum > startTimeHour;

        return hasSlots && isAfterMinTime && isAfterStartTime;
      })
      .map(([hour]) => `${hour}:00`);

    return availableTimes;

  }, [selectedStartDate, selectedEndDate, startTimeHour]);

  /**
   * Updates error states based on available times
   */
  const updateErrorStates = useCallback((times) => {
    const hasSlots = times.length > 0;

    if (setNoSlotError) {
      setNoSlotError(hasSlots ? "" : "No slots available for this date. Please select a different end date.");
    }

    if (setIsReserveEnable) {
      setIsReserveEnable(!hasSlots);
    }
  }, [setNoSlotError, setIsReserveEnable]);

  /**
   * Sets default time values when no explicit values are provided or when current values are invalid
   */
  const setDefaultTimeValues = useCallback((times) => {
    if (!times.length) return;

    const hasTimeCookie = Boolean(get_cookie("timerange"));
    const defaultTime = PREFERRED_DEFAULT_TIMES.find(time => times.includes(time)) || times[0];
    // A time carried over in the timerange cookie is normally kept as-is,
    // but never when it is already in the past for the selected date.
    const isPastTime = (time) => Boolean(time) && !isTimeBookable(selectedDate, time);
    const needsDefault = (time) =>
      hasTimeCookie ? isPastTime(time) : (!time || !times.includes(time));

    // Set start time default if not set or not available in current times
    if (setStartTimeDefaultValue && needsDefault(startTimeDefaultValue)) {
      setStartTimeDefaultValue(defaultTime);
    }

    // Set end time default if not set or not available in current times (but only if not in edit mode)
    if (setEndTimeDefaultValue && !isVehicleEdit && needsDefault(endTimeDefaultValue)) {
      setEndTimeDefaultValue(defaultTime);
    }
  }, [
    selectedDate,
    isVehicleEdit,
    startTimeDefaultValue,
    endTimeDefaultValue,
    setStartTimeDefaultValue,
    setEndTimeDefaultValue
  ]);

  /**
   * Determines if time filtering should be updated
   */
  const shouldUpdateTimes = useMemo(() => {
    if (!selectedDate || !vehicleSlot?.available_dates) return false;

    const startTimeChanged = prevStartTime.current !== startTimeHour;
    const startDateChanged = prevStartDate.current !== selectedStartDate;
    const endDateChanged = prevEndDate.current !== selectedEndDate;
    const hasCookie = Boolean(get_cookie("timerange"));
    const isNotSubmitEvent = vehicleSlot?.event !== "submit";

    return isNotSubmitEvent && (
      (!isShowCalendar && startTimeChanged) ||
      (hasCookie && !hasInitialized.current) ||
      startDateChanged ||
      endDateChanged
    );
  }, [
    selectedDate,
    vehicleSlot,
    startTimeHour,
    selectedStartDate,
    selectedEndDate,
    isShowCalendar
  ]);

  // Effect for updating filtered times
  useEffect(() => {
    if (!shouldUpdateTimes) return;

    const times = getFilteredTimeOptions(selectedDate, vehicleSlot.available_dates);

    setFilteredTimes(times);
    updateErrorStates(times);

    // Update ref values
    prevStartDate.current = selectedStartDate;
    prevEndDate.current = selectedEndDate;
    prevStartTime.current = startTimeHour;

    if (!hasInitialized.current) {
      hasInitialized.current = true;
    }
  }, [shouldUpdateTimes, selectedDate, vehicleSlot, getFilteredTimeOptions, updateErrorStates]);

  // Effect for setting default values
  useEffect(() => {
    if (!selectedDate || !filteredTimes.length) {
      if (!get_cookie("timerange") && setEndTimeDefaultValue && !vehicleSlug) {
        setEndTimeDefaultValue("");
      }
      return;
    }

    // Check if current end time default value is still available in filtered times
    const isCurrentEndTimeAvailable = endTimeDefaultValue && filteredTimes.includes(endTimeDefaultValue);

    // If end time is not available or not set, update it
    const isCurrentEndTimePast = endTimeDefaultValue && !isTimeBookable(selectedDate, endTimeDefaultValue);
    if (
      setEndTimeDefaultValue &&
      ((!isCurrentEndTimeAvailable && !get_cookie("timerange")) || isCurrentEndTimePast)
    ) {
      const defaultTime = PREFERRED_DEFAULT_TIMES.find(time => filteredTimes.includes(time)) || filteredTimes[0];
      setEndTimeDefaultValue(defaultTime);
    }

    // Set initial default values if none exist
    setDefaultTimeValues(filteredTimes);
  }, [selectedDate, filteredTimes, endTimeDefaultValue, setDefaultTimeValues, vehicleSlug, setEndTimeDefaultValue]);

  // Effect for syncing with defaultValue prop
  useEffect(() => {
    setSelectedValue(defaultValue || "");
  }, [defaultValue]);

  /**
   * Handles time selection changes
   */
  const handleTimeChange = useCallback((event) => {
    const value = event.target.value;
    setSelectedValue(value);

    if (parentTimeChangeHandler) {
      parentTimeChangeHandler(value, currentObjName);
    }
  }, [parentTimeChangeHandler, currentObjName]);

  /**
   * Formats time for display in 12-hour format
   */
  const formatTimeForDisplay = useCallback((time, date) => {
    const formattedDate = moment(date, "MMM DD, YYYY").format("YYYY-MM-DD");
    const timeString = time?.padStart(5, "0");

    if (!timeString || !moment(timeString, "HH:mm", true).isValid()) {
      return null;
    }

    const dateTimeString = `${formattedDate} ${timeString}`;
    return moment(dateTimeString, "YYYY-MM-DD HH:mm").format("h:mm A");
  }, []);

  // Memoized time options for rendering
  const timeOptions = useMemo(() => {
    if (!filteredTimes.length || !selectedDate) return [];

    return filteredTimes
      .map((time, index) => {
        const formattedTime = formatTimeForDisplay(time, selectedDate);
        return formattedTime ? { value: time, label: formattedTime, key: index } : null;
      })
      .filter(Boolean);
  }, [filteredTimes, selectedDate, formatTimeForDisplay]);

  // Determine CSS classes
  const formGroupClasses = useMemo(() => {
    const baseClasses = "floating-form-group select-option";
    const hasValue = defaultValue || selectedValue || selectedDate;
    return hasValue ? `${baseClasses} value-selected` : baseClasses;
  }, [defaultValue, selectedValue, selectedDate]);

  return (
    <Form.Group controlId="time.ControlSelect" className={formGroupClasses}>
      <Form.Control
        as="select"
        value={selectedValue}
        className="floating-input"
        onChange={handleTimeChange}
      >
        {timeOptions.map(({ value, label, key }) => (
          <option value={value} key={key}>
            {label}
          </option>
        ))}
      </Form.Control>
      <Form.Label className="floating-label">Time*</Form.Label>
    </Form.Group>
  );
}

export default React.memo(TimePicker);
